<template>
  <div
    class="p-6 relative bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full"
  >
    <!-- Close Button -->
    <button
      @click="$emit('update:staffModal', false)"
      class="absolute top-4 right-4 h-8 w-8 flex items-center justify-center rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      type="button"
      aria-label="Close modal"
    >
      <i class="fa-solid fa-xmark text-lg"></i>
    </button>

    <!-- Modal Header -->
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">
        Add Staff Members
      </h2>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        Add staff members by their email address
      </p>
    </div>

    <!-- Warning Alert -->
    <div
      class="flex items-start p-3 mb-6 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
    >
      <i
        class="fa-solid fa-circle-exclamation text-amber-500 dark:text-amber-400 mt-0.5 mr-2"
      ></i>
      <p class="text-sm text-amber-800 dark:text-amber-300">
        Staff members will have limited access. Please verify emails carefully.
      </p>
    </div>

    <!-- Email Input Section -->
    <div class="space-y-2">
      <label
        for="staff-email"
        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        Email Address
      </label>
      <div class="relative flex items-center">
        <input
          autofocus
          id="staff-email"
          v-model="staffEmail"
          @keyup.enter="addStaff"
          type="email"
          placeholder="staff@example.com"
          :class="{
            'block w-full rounded-lg border py-3 px-4 pr-24 text-sm shadow-sm focus:outline-none focus:ring-2 transition-all': true,
            'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500/20':
              !emailError,
            'border-red-500 dark:border-red-400 text-red-600 dark:text-red-400 placeholder-red-400 dark:placeholder-red-500/60 bg-red-50 dark:bg-red-900/10 focus:border-red-500 focus:ring-red-500/20':
              emailError,
          }"
          aria-describedby="email-error"
        />
        <button
          @click="addStaff"
          :disabled="isAdding"
          class="absolute right-1.5 flex items-center justify-center px-3 py-2 bg-button dark:bg-sub-800 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="!isAdding" class="flex items-center">
            <i class="fa-solid fa-plus mr-1.5 text-xs"></i>
            Add
          </span>
          <span v-else class="flex items-center">
            <i class="fa-solid fa-spinner fa-spin mr-1.5 text-xs"></i>
            Adding...
          </span>
        </button>
      </div>

      <!-- Error Message -->
      <p
        v-if="emailError"
        id="email-error"
        class="text-sm text-red-600 dark:text-red-400"
      >
        {{ emailError }}
      </p>

      <!-- Success Message -->
      <p v-if="emailSuccess" class="text-sm text-green-600 dark:text-green-400">
        <i class="fa-solid fa-check-circle mr-1"></i> {{ emailSuccess }}
      </p>
    </div>

    <!-- Added Emails Preview -->
    <div v-if="addedEmails.length > 0" class="mt-6">
      <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Emails to be activated
      </h3>
      <div class="space-y-2">
        <div
          v-for="(email, index) in addedEmails"
          :key="index"
          class="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg"
        >
          <span class="text-sm text-gray-700 dark:text-gray-300">{{
            email
          }}</span>
          <button
            @click="removeEmail(index)"
            class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
            aria-label="Remove email"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Submit Button -->
    <div class="mt-6">
      <button
        @click="submitEmails"
        :disabled="addedEmails.length === 0 || isSubmitting"
        class="w-full py-3 px-4 bg-button dark:bg-sub-800 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span v-if="!isSubmitting"
          >Activate {{ addedEmails.length }} Staff Email(s)</span
        >
        <span v-else class="flex items-center justify-center">
          <i class="fa-solid fa-spinner fa-spin mr-2"></i> Processing...
        </span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "AddStaffModal",
  props: {
    staffModal: {
      type: Boolean,
      required: true,
    },
    addedEmails: {
      type: Array,
      required: true,
    },
  },
  emits: ["update:staffModal", "addStaff", "removeEmail", "submitEmails"],
  data() {
    return {
      staffEmail: "",
      emailError: null,
      emailSuccess: null,
      isAdding: false,
      isSubmitting: false,
    };
  },
  methods: {
    validateEmail(email) {
      email = email.trim();
      if (!email) return "Please enter an email address";
      if (/\s/.test(email)) return "Email should not contain spaces";
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!re.test(email)) return "Please enter a valid email address";
      if (this.addedEmails.includes(email))
        return "This email has already been added";
      return null;
    },
    addStaff() {
      this.emailError = null;
      this.emailSuccess = null;
      const error = this.validateEmail(this.staffEmail);
      if (error) {
        this.emailError = error;
        return;
      }
      this.isAdding = true;
      setTimeout(() => {
        const cleanEmail = this.staffEmail.trim();
        this.$emit("addStaff", cleanEmail);
        this.emailSuccess = `${cleanEmail} added successfully`;
        this.staffEmail = "";
        this.isAdding = false;
        setTimeout(() => {
          this.emailSuccess = null;
        }, 3000);
      }, 500);
    },
    removeEmail(index) {
      this.$emit("removeEmail", index);
    },
    submitEmails() {
      this.isSubmitting = true;
      setTimeout(() => {
        this.$emit("submitEmails");
        this.isSubmitting = false;
        this.$emit("update:staffModal", false);
      }, 1500);
    },
  },
};
</script>
